import { RESET_ORGANIZATION_STATE } from "@overmap-ai/core"
import { createFileRoute, Outlet } from "@tanstack/react-router"

import { sdk } from "../../sdk"
import { appStore, setActiveOrganizationId } from "../../store"

/* Keep track of when the data has been loaded once throughout the lifetime of the application running,
 * this allows for the route loader to only load the data when the projectId path param has changed or the app is
 * refreshed */
let LOADED_ONCE = false

/* Manual AbortController management for deferred data loading.
 * TanStack Router's abortController only triggers when the loader promise resolves,
 * but with deferred data the loader returns immediately, so we manage our own. */
let PREV_ABORT_CONTROLLER: AbortController | null = null

export const Route = createFileRoute("/_loggedIn/$organizationId")({
	component: Outlet,
	gcTime: 0,
	preload: false,
	pendingMs: 0,
	shouldReload: ({ cause }) => {
		return cause === "enter"
	},
	loader: async ({ params, context }) => {
		const { organizationId } = params
		const { activeOrganizationId } = context

		console.debug("$organizationId loader running: ", organizationId)

		if (LOADED_ONCE && activeOrganizationId === organizationId) return

		// Abort any in-flight request from a previous navigation
		if (PREV_ABORT_CONTROLLER) PREV_ABORT_CONTROLLER.abort()

		// Create a new AbortController for this request
		const abortController = new AbortController()
		PREV_ABORT_CONTROLLER = abortController

		LOADED_ONCE = true

		appStore.dispatch(setActiveOrganizationId(organizationId))
		if (activeOrganizationId !== organizationId) appStore.dispatch(RESET_ORGANIZATION_STATE())

		// READY TO START ORGANIZATION DATA FETCH
		// 1) Fetch and await organization data expecting to be awaited
		// 2) Fetch and allow lazy organization data to resolve when it does

		const fetchLazyOrganizationData = await sdk.main.fetchOrganizationData(organizationId, abortController.signal)

		const lazyOrganizationData = fetchLazyOrganizationData()

		void lazyOrganizationData.finally(() => {
			if (PREV_ABORT_CONTROLLER === abortController) {
				PREV_ABORT_CONTROLLER = null
				console.debug("cleared $organizationId loader abort controller: ", organizationId)
			}
		})

		return
	},
})
